import { useState } from 'react'
import { useMedia } from 'react-use'
import { VisuallyHidden } from '@overdose/components'
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react'
import classNames from 'classnames'
import type { Swiper as SwiperType } from 'swiper'
import { Controller } from 'swiper/modules'
import { Button } from '~/components'
import Carousel from '~/components/Carousel'
import { Image } from '~/components/Image'
import { SectionContainer } from '~/components/SectionContainer'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { VideoPlayer } from '~/components/VideoPlayer'
import styles from './VideoCarousel.module.css'
import { VideoCarouselProps } from './VideoCarousel.types'

const CURRENT_SLIDE_OFFSET = 1
const MOBILE_SLIDE_COUNT = 2
const TABLET_SLIDE_COUNT = 3
const DESKTOP_SLIDE_COUNT = 4

export const VideoCarousel = ({
  width = 'wide',
  heading,
  videos,
  muted,
  autoplay,
}: VideoCarouselProps) => {
  const [swiperController, setSwiperController] = useState<SwiperType | null>(
    null
  )

  const [isBeginning, setIsBeginning] = useState(videos.length > 0)
  const [isEnd, setIsEnd] = useState(false)
  const [firstIndex, setFirstIndex] = useState(0)
  const [activeSlide, setActiveSlide] = useState(0)

  const isMobile = useMedia('(max-width: 767px)', false)
  const isTablet = useMedia('(min-width: 768px) and (max-width: 1023px)', false)

  const videoCount = videos?.length
  const currentSlide = firstIndex
  let visibleLastSlide = currentSlide
  if (isMobile) {
    visibleLastSlide += MOBILE_SLIDE_COUNT
  } else if (isTablet) {
    visibleLastSlide += TABLET_SLIDE_COUNT
  } else {
    visibleLastSlide += DESKTOP_SLIDE_COUNT
  }
  if (visibleLastSlide > videoCount) {
    visibleLastSlide = videoCount
  }

  const currentVideo = videos?.[activeSlide]

  return (
    <SectionContainer
      width={width}
      wrapperClassName={`${heading ? '!my-10 lg:!my-8 xl:!my-16' : ''}`}
      innerClassName={styles.root}>
      <Typography
        tag={TypographyTag.p}
        variant={TypographyVariant.Heading3}
        className={classNames('text-center', styles.title)}>
        {heading}
      </Typography>

      {!!videoCount && (
        <div className='grid grid-cols-4 md:grid-cols-12 relative gap-x-4 gap-y-6 items-center mt-6 lg:mt-8 xl:mt-10'>
          <div className='col-span-4 2xl:col-span-8 3xl:col-span-6 md:col-span-10 md:col-start-2 2xl:col-start-3 3xl:col-start-4 row-start-1 w-full lg:mb-2 xl:mb-4 rounded-xl'>
            <VideoPlayer
              source={[currentVideo]}
              muted={muted}
              autoplay={autoplay}
            />
          </div>
          <div className='relative col-span-4 2xl:col-span-8 3xl:col-span-6 md:col-span-10 md:col-start-2 2xl:col-start-3 3xl:col-start-4 row-start-2'>
            <Carousel.Container
              modules={[Controller]}
              className={styles.slider}
              onSwiper={setSwiperController}
              slidesPerView={2}
              spaceBetween={16}
              breakpoints={{
                '1024': {
                  slidesPerView: 4,
                },
                '768': {
                  slidesPerView: 3,
                },
              }}
              slideToClickedSlide
              grabCursor
              wrapperClass='no-use pl-0.5'
              centerInsufficientSlides
              loop={false}
              onClick={(sw) => {
                setActiveSlide(sw.clickedIndex)
              }}
              onReachBeginning={(sw) => {
                setIsBeginning(sw.isBeginning)
                setIsEnd(sw.isEnd)
              }}
              onReachEnd={(sw) => {
                setIsBeginning(sw.isBeginning)
                setIsEnd(sw.isEnd)
              }}
              onRealIndexChange={(sw) => {
                setFirstIndex(sw.activeIndex)
                setIsBeginning(sw.isBeginning)
                setIsEnd(sw.isEnd)
              }}>
              {videos.map(({ title, thumbnail, alt }, index) => {
                const isActive = activeSlide === index

                return (
                  <Carousel.Slide key={index}>
                    <div
                      className={classNames(
                        'flex flex-col outline-2 outline-text-action-link-hover rounded opacity-75 py-1',
                        {
                          'outline !opacity-100 shadow-lg p-1': isActive,
                        }
                      )}>
                      <div className='relative aspect-video rounded-t overflow-clip'>
                        <Image src={thumbnail} alt={alt} fill />
                      </div>
                      <Typography
                        tag={TypographyTag.p}
                        variant={TypographyVariant.BodySmallMedium}
                        className={classNames('my-3 px-2 line-clamp-1')}>
                        {title}
                      </Typography>
                    </div>
                  </Carousel.Slide>
                )
              })}
            </Carousel.Container>
          </div>

          <Button
            onClick={() => {
              if (!swiperController?.isBeginning) {
                swiperController.slidePrev()
              }
            }}
            disabled={isBeginning}
            theme={{
              root: classNames(
                'shadow-md !border-0 col-start-1 2xl:col-start-2 3xl:col-start-3 row-start-3 md:row-start-2  md:justify-self-end',
                styles.btn
              ),
            }}
            title='previous'
            size='sm'
            shape='circle'
            icon={<IconArrowLeft />}>
            <VisuallyHidden>previous</VisuallyHidden>
          </Button>
          <Button
            onClick={() => {
              if (!swiperController?.isEnd) {
                swiperController.slideNext()
              }
            }}
            disabled={isEnd}
            theme={{
              root: classNames(
                'shadow-md !border-0 col-start-4 md:col-start-12 2xl:col-start-11 3xl:col-start-10 max-md:justify-self-end  row-start-3 md:row-start-2',
                styles.btn
              ),
            }}
            title='next'
            size='sm'
            shape='circle'
            icon={<IconArrowRight />}>
            <VisuallyHidden>next</VisuallyHidden>
          </Button>
          <Typography
            tag={TypographyTag.p}
            variant={TypographyVariant.BodyRegularExtraLineHeight}
            className='text-primary-muted row-start-3 col-span-2 md:col-span-12 justify-self-center self-center min-wmax'>
            {`Showing ${
              firstIndex + CURRENT_SLIDE_OFFSET
            }-${visibleLastSlide} of ${videoCount} videos`}
          </Typography>
        </div>
      )}
    </SectionContainer>
  )
}
